import InertiaLink from '@/Components/Common/InertiaLink.jsx';
import {PagesStyle} from '@/Styles/pages.js';
import {Link, usePage} from '@inertiajs/react';
import {Box, Button, Drawer, Link as MuiLink, Portal} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import * as React from 'react';
import {memo, useState} from 'react';

const MobileMenuWrapper = ({current_url, menuItems, toggleDrawer}) => {
    return (
        <Box role="presentation" onClick={toggleDrawer(false)}>
            <Box component="nav">
                <Box component="ul" sx={mobile_styles.menu_list}>
                    {menuItems.map(({text, route, method, external}) => (
                        <Box key={text} component="li" sx={mobile_styles.menu_list_item}>
                            <Button
                                component={InertiaLink}
                                color="inherit"
                                className={current_url === route ? 'active' : ''}
                                fullWidth
                                href={route}
                                method={method}
                                external={external}
                                sx={mobile_styles.menu_button}
                            >
                                {text}
                            </Button>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const MainMenu = ({menuItems}) => {
    const [open, setOpen] = useState(false);
    const {props: page_props} = usePage();
    const {location: current_url} = page_props.ziggy;

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };

    return (
        <>
            <Box component="nav" className="gs18-HeaderNav">
                <Box component="ul" className="gs18-HeaderNav__Menu">
                    {menuItems.map(({id, text, route, method, external}) => (
                        <Box
                            key={'desktop-header-nav-list-item-' + id}
                            component="li"
                            className="gs18-HeaderNav__Item"
                        >
                            {method ? (
                                <Button
                                    component={InertiaLink}
                                    variant="text"
                                    color="inherit"
                                    className={current_url === route ? 'active' : ''}
                                    fullWidth
                                    href={route}
                                    method={method}
                                    external={external}
                                    sx={desktopMenuLink}
                                >
                                    {text}
                                </Button>
                            ) : (
                                <MuiLink
                                    component={Link}
                                    color="inherit"
                                    underline="none"
                                    className={current_url === route ? 'active' : ''}
                                    href={route}
                                    method={method}
                                    sx={desktopMenuLink}
                                >
                                    {text}
                                </MuiLink>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box component="nav" sx={mobile_styles.trigger_container}>
                <Button
                    id="main-button"
                    aria-controls={open ? 'main-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : 'false'}
                    onClick={toggleDrawer(!open)}
                    sx={{
                        ...mobile_styles.trigger,
                        backgroundImage: `url(/images/icons/${open ? 'close.svg' : 'burger-menu.svg'})`,
                    }}
                >
                    <Box component="span" sx={visuallyHidden}>
                        Menu
                    </Box>
                </Button>

                <Portal container={document.body}>
                    <Drawer
                        anchor="right"
                        open={open}
                        ModalProps={{
                            keepMounted: false,
                        }}
                        sx={mobile_styles.menu}
                        onClose={toggleDrawer(false)}
                    >
                        <MobileMenuWrapper
                            current_url={current_url}
                            menuItems={menuItems}
                            toggleDrawer={toggleDrawer}
                        />
                    </Drawer>
                </Portal>
            </Box>
        </>
    );
};

const mobile_styles = {
    trigger_container: {
        display: 'inline-block',
        height: '100%',
    },
    trigger: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        minWidth: 'auto',
        height: '100%',
        textAlign: 'center',
        borderLeft: '1px solid #e1e1e1',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '50%',
        backgroundSize: 21,
        backgroundImage: 'url(/images/icons/burger-menu.svg)',
        borderRadius: 0,
        ['@media (min-width: 980px)']: {
            display: 'none',
        },
    },
    menu: {
        top: 65,
        '& .MuiModal-backdrop': {
            top: 65,
        },
        '& .MuiDrawer-paper': {
            top: 65,
            width: '100%',
        },
    },
    menu_wrapper: {
        width: '100%',
    },
    menu_list: {
        ...PagesStyle.list,
    },
    menu_list_item: {
        ...PagesStyle.list_item,
        display: 'block',
        borderBottom: '1px solid #e1e1e1',
        ':hover': {
            backgroundColor: '#e1e1e1',
        },
    },
    menu_button: {
        justifyContent: 'space-between',
        gap: 1,
        p: [3, 2],
        width: '100%',
        minHeight: 85,
        borderRadius: 0,
        '&.active': {
            backgroundColor: 'black',
            color: 'white',
        },
    },
};

const desktopMenuLink = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    px: 1.875,
    py: 0,
    height: '100%',
    fontFamily: '"Roboto", "Arial"',
    fontSize: '1.125rem',
    fontWeight: 500,
    textAlign: 'center',
    backgroundColor: 'transparent',
    '&:hover': {
        color: theme.palette.process_black.main,
        backgroundColor: 'transparent',
    },
    '&.active': {
        backgroundColor: 'black',
        color: 'white',
    },
});


export default memo(MainMenu);
